import {
  resolveArrowDimensions,
  arrowStrokeTerminalGap,
} from "./arrowGeometry.js";
import {
  layoutFuturesWheel,
  wheelNodeSize,
  futuresWheelEdgePath,
  futuresWheelDomainPath,
} from "./futuresWheelLayout.js";
import {
  futuresWheelContrastAccent,
  futuresWheelLevelAppearance,
  futuresWheelSectorAppearance,
} from "./futuresWheelTheme.js";

/** Bounded geometry cache: descriptions, resources and collaborator state cannot invalidate layout. */
export function createWheelLayoutCache(
  compute = layoutFuturesWheel,
  limit = 8,
) {
  const entries = new Map();
  return (nodes, edges) => {
    const key = JSON.stringify([
      nodes.map((node) => [
        String(node.id),
        node.data?.label,
        node.data?.semantic_data?.futures_wheel,
        node.data?.semantic_data?.collapsed,
      ]),
      edges.map((edge) => [String(edge.source), String(edge.target)]),
    ]);
    if (entries.has(key)) {
      const value = entries.get(key);
      entries.delete(key);
      entries.set(key, value);
      return value;
    }
    const layout = compute(nodes, edges);
    entries.set(key, layout);
    if (entries.size > limit) entries.delete(entries.keys().next().value);
    return layout;
  };
}
export const getWheelLayout = createWheelLayoutCache();
export const WHEEL_TYPOGRAPHY = Object.freeze({
  fontSize: 15,
  fontWeight: 650,
  lineHeight: 17,
});
export function wrapWheelLabel(value, size) {
  const limit = Math.floor((size.width - 24) / 8),
    lines = [];
  let line = "";
  for (const word of String(value || "Untitled consequence").split(/\s+/)) {
    const candidate = `${line} ${word}`.trim();
    if (candidate.length > limit && line) {
      lines.push(line);
      line = word;
    } else line = candidate;
  }
  if (line) lines.push(line);
  const shown = lines.slice(0, 3);
  if (lines.length > 3) shown[2] = `${shown[2].slice(0, limit - 1)}…`;
  return shown;
}
export function createWheelScene({
  nodes,
  edges,
  allEdges = edges,
  layout = getWheelLayout(nodes, edges),
  theme = {},
}) {
  const byId = new Map(nodes.map((node) => [String(node.id), node]));
  const children = new Map();
  for (const edge of allEdges) {
    const key = String(edge.source);
    if (!children.has(key)) children.set(key, []);
    children.get(key).push(String(edge.target));
  }
  const root = nodes.find((node) => Number(node.data?.semantic_data?.futures_wheel?.order) === 0);
  const domains = root?.data?.semantic_data?.futures_wheel?.domains || [];
  const domainIndices = new Map(domains.map((domain, index) => [String(domain.id), index]));
  const firstOrderNodes = nodes
    .filter((node) => Number(node.data?.semantic_data?.futures_wheel?.order) === 1)
    .sort((first, second) => {
      const a = layout.positions.get(String(first.id));
      const b = layout.positions.get(String(second.id));
      return Math.atan2(a.y - layout.centerY, a.x - layout.centerX) - Math.atan2(b.y - layout.centerY, b.x - layout.centerX);
    });
  const branchIndices = new Map(firstOrderNodes.map((node, index) => [String(node.id), index]));
  const branchFor = (nodeId) => {
    let current = byId.get(String(nodeId));
    const visited = new Set();
    while (current && !visited.has(String(current.id))) {
      visited.add(String(current.id));
      const order = Number(current.data?.semantic_data?.futures_wheel?.order) || 0;
      if (order <= 1) return current;
      current = byId.get(String(current.data?.semantic_data?.futures_wheel?.parent_id));
    }
    return null;
  };
  const branchColor = (nodeId) => {
    const branch = branchFor(nodeId);
    if (!branch) return theme.text || "#52616b";
    const domainId = branch.data?.semantic_data?.futures_wheel?.domain_id;
    const index = domainId != null && domainIndices.has(String(domainId))
      ? domainIndices.get(String(domainId))
      : branchIndices.get(String(branch.id)) || 0;
    const paletteColor = theme.palette?.[index % (theme.palette?.length || 1)] || theme.text || "#52616b";
    return futuresWheelContrastAccent(theme, paletteColor, 0.98);
  };
  const count = (id) => {
    const seen = new Set(),
      pending = [...(children.get(String(id)) || [])];
    while (pending.length) {
      const child = pending.pop();
      if (seen.has(child)) continue;
      seen.add(child);
      pending.push(...(children.get(child) || []));
    }
    return seen.size;
  };
  return {
    layout,
    nodes: nodes.flatMap((node) => {
      const point = layout.positions.get(String(node.id));
      if (!point) return [];
      const order = node.data?.semantic_data?.futures_wheel?.order || 0,
        size = wheelNodeSize(order);
      return [
        {
          node,
          point,
          order,
          size,
          lines: wrapWheelLabel(node.data?.label, size),
          appearance: futuresWheelLevelAppearance(theme, order),
          foldedCount: node.data?.semantic_data?.collapsed ? count(node.id) : 0,
        },
      ];
    }),
    edges: edges.flatMap((edge) => {
      const start = layout.positions.get(String(edge.source)),
        end = layout.positions.get(String(edge.target));
      if (!start || !end) return [];
      const original = futuresWheelEdgePath(
        start,
        end,
        byId.get(String(edge.source))?.data?.semantic_data?.futures_wheel
          ?.order,
        byId.get(String(edge.target))?.data?.semantic_data?.futures_wheel
          ?.order,
      );
      const [x1, y1, x2, y2] = original
        .match(/-?(?:\d+\.?\d*|\.\d+)(?:e[-+]?\d+)?/gi)
        .map(Number);
      const length = Math.hypot(x2 - x1, y2 - y1) || 1,
        ux = (x2 - x1) / length,
        uy = (y2 - y1) / length;
      const dimensions = resolveArrowDimensions(12, 2.8),
        gap = arrowStrokeTerminalGap(2.8);
      return [
        {
          edge,
          order: byId.get(String(edge.target))?.data?.semantic_data?.futures_wheel?.order || 1,
          color: branchColor(edge.target),
          path: `M ${x1} ${y1} L ${x2 - ux * gap} ${y2 - uy * gap}`,
          arrow: `M ${x2} ${y2} L ${x2 - ux * dimensions.length - uy * dimensions.halfWidth} ${y2 - uy * dimensions.length + ux * dimensions.halfWidth} L ${x2 - ux * dimensions.length + uy * dimensions.halfWidth} ${y2 - uy * dimensions.length - ux * dimensions.halfWidth} Z`,
        },
      ];
    }),
    sectors: (layout.domainSectors || []).map((sector, index) => ({
      ...sector,
      path: futuresWheelDomainPath(layout, sector),
      heading: layout.domainLabelLayout?.labelsById.get(sector.id),
      ...futuresWheelSectorAppearance(theme, futuresWheelContrastAccent(
        theme,
        theme.palette?.[index % (theme.palette?.length || 1)] || theme.focalBorder || "#315f90",
        0.95,
      )),
    })),
  };
}
